import { STYLES } from './styles'
import { MARK_SVG } from './assets'

/** Locales the built-in copy ships in. */
export type BananaGamesLoaderLocale = 'pt' | 'es' | 'en'

export interface BananaGamesLoaderOptions {
  /** How long the screen holds at minimum, in milliseconds. Default 3000. */
  duration?: number
  /** Text shown while loading. Defaults to the built-in copy for the resolved locale. */
  label?: string
  /** Text shown once the timer elapses. Defaults to the built-in copy. */
  doneLabel?: string
  /** Muted text shown next to `doneLabel`, if any. */
  doneHint?: string
}

const LOCALES: readonly BananaGamesLoaderLocale[] = ['pt', 'es', 'en']
const FALLBACK_LOCALE: BananaGamesLoaderLocale = 'en'

/**
 * The whole dictionary — two strings per locale.
 *
 * The copy plays on the brand rather than saying "loading": the mark is already
 * on screen, so the bar has room for something with a wink in it. Brand-tied
 * instead of game-tied on purpose — the same screen opens the operator panels,
 * where "setting up the round" would read as nonsense.
 */
const DICT: Record<BananaGamesLoaderLocale, { label: string; doneLabel: string }> = {
  pt: { label: 'Descascando a banana...', doneLabel: 'Bora jogar!' },
  es: { label: 'Pelando la banana...', doneLabel: '¡A jugar!' },
  en: { label: 'Peeling the banana...', doneLabel: "Let's play!" },
}

const DEFAULTS = {
  duration: 3000,
  doneHint: '',
} as const

/** Coerce any language tag ("pt-BR", "EN", " es ") to a supported locale. */
function normalizeLocale(value: string | null | undefined): BananaGamesLoaderLocale | null {
  if (!value) return null
  const code = value.trim().slice(0, 2).toLowerCase()
  return (LOCALES as readonly string[]).includes(code)
    ? (code as BananaGamesLoaderLocale)
    : null
}

/**
 * `?lang=` from the page URL — the ecosystem convention (the games read the same
 * param). This is what makes the loader self-sufficient: it picks the player's
 * language on its own, without the host having to pass anything in.
 */
function localeFromUrl(): BananaGamesLoaderLocale | null {
  if (typeof window === 'undefined') return null
  try {
    return normalizeLocale(new URLSearchParams(window.location.search).get('lang'))
  } catch {
    return null
  }
}

/** `<html lang>` — apps that switch language in place keep this in sync. */
function localeFromDocument(): BananaGamesLoaderLocale | null {
  if (typeof document === 'undefined') return null
  return normalizeLocale(document.documentElement.getAttribute('lang'))
}

/** Ambient sparks. Positions come from the index, never random: a re-render
 *  must not teleport a spark that is halfway through its drift. */
const SPARK_COUNT = 14

/**
 * `<banana-games-loader>` — the Banana Games opening screen as a self-contained
 * custom element. Same bar, same spinner riding it, same timing as the original
 * Vue component; the mark, styles and spinner all live in the shadow root, so it
 * drops into any page (framework or not) with nothing to wire up.
 *
 * Copy is built in and localised by the element itself — it reads `?lang=` from
 * the URL (falling back to `<html lang>`, then English), so a host never has to
 * pass text or a locale in. An explicit `label` / `doneLabel` still wins.
 *
 * Fires a `finished` event (and resolves the `finished` promise) once `duration`
 * elapses. It does NOT remove itself — whoever showed it decides when the app is
 * actually ready, which is usually later than this timer. Call `hide()` then.
 */
export class BananaGamesLoaderElement extends HTMLElement {
  static get observedAttributes(): string[] {
    return ['duration', 'label', 'done-label', 'done-hint', 'lang']
  }

  /** Resolves once `duration` has elapsed and the `finished` event has fired. */
  readonly finished: Promise<void>

  private _resolveFinished!: () => void
  private _built = false
  private _finished = false
  private _remaining = 0
  private _timeout: ReturnType<typeof setTimeout> | null = null
  private _interval: ReturnType<typeof setInterval> | null = null

  private _banana: SVGElement | null = null
  private _text: HTMLParagraphElement | null = null

  // Property backing — when set, wins over the matching attribute.
  private _durationProp?: number
  private _labelProp?: string
  private _doneLabelProp?: string
  private _doneHintProp?: string

  constructor() {
    super()
    this.attachShadow({ mode: 'open' })
    this.finished = new Promise<void>((resolve) => {
      this._resolveFinished = resolve
    })
  }

  // ---- configuration (property + attribute, property wins) -----------------

  get duration(): number {
    if (this._durationProp != null) return this._durationProp
    const raw = this.getAttribute('duration')
    const n = raw != null ? Number(raw) : NaN
    return Number.isFinite(n) && n >= 0 ? n : DEFAULTS.duration
  }
  set duration(value: number) {
    this._durationProp = value
    if (this.isConnected) this._applyDurationVar()
  }

  /**
   * The locale the built-in copy is shown in, resolved by the element itself:
   * an explicit `lang` attribute, else `?lang=` from the URL, else `<html lang>`,
   * else English. Nothing has to be passed in at instantiation.
   */
  get locale(): BananaGamesLoaderLocale {
    return (
      normalizeLocale(this.getAttribute('lang')) ??
      localeFromUrl() ??
      localeFromDocument() ??
      FALLBACK_LOCALE
    )
  }

  get label(): string {
    return this._labelProp ?? this.getAttribute('label') ?? DICT[this.locale].label
  }
  set label(value: string) {
    this._labelProp = value
    if (this.isConnected) this._renderText()
  }

  get doneLabel(): string {
    return this._doneLabelProp ?? this.getAttribute('done-label') ?? DICT[this.locale].doneLabel
  }
  set doneLabel(value: string) {
    this._doneLabelProp = value
    if (this.isConnected) this._renderText()
  }

  get doneHint(): string {
    return this._doneHintProp ?? this.getAttribute('done-hint') ?? DEFAULTS.doneHint
  }
  set doneHint(value: string) {
    this._doneHintProp = value
    if (this.isConnected) this._renderText()
  }

  // ---- lifecycle -----------------------------------------------------------

  connectedCallback(): void {
    this._build()
    this._applyDurationVar()
    this._finished = false
    this._remaining = this.duration
    this._renderText()

    this._clearTimers()
    this._timeout = setTimeout(() => this._finish(), this.duration)
    this._interval = setInterval(() => this._tick(), 500)
  }

  disconnectedCallback(): void {
    this._clearTimers()
  }

  attributeChangedCallback(name: string): void {
    if (!this.isConnected) return
    if (name === 'duration') this._applyDurationVar()
    else this._renderText()
  }

  // ---- imperative API ------------------------------------------------------

  /**
   * Fade the screen out and remove it from the DOM. Resolves once gone. This is
   * separate from `finished` on purpose: the timer says "the intro is over",
   * this says "the app is ready — take the screen away".
   */
  hide(): Promise<void> {
    return new Promise<void>((resolve) => {
      if (!this.isConnected) {
        resolve()
        return
      }
      let done = false
      const finish = (): void => {
        if (done) return
        done = true
        this.removeEventListener('transitionend', onEnd)
        this.remove()
        resolve()
      }
      const onEnd = (event: TransitionEvent): void => {
        if (event.target === this && event.propertyName === 'opacity') finish()
      }
      this.addEventListener('transitionend', onEnd)
      // Fallback in case the transition never fires (reduced motion, display
      // changes, an already-hidden host).
      setTimeout(finish, 450)
      // Next frame, so the browser has a starting opacity to animate from.
      requestAnimationFrame(() => this.classList.add('is-hiding'))
    })
  }

  // ---- internals -----------------------------------------------------------

  private _tick(): void {
    if (this._banana) {
      const current = this._banana.getAttribute('fill-opacity')
      this._banana.setAttribute('fill-opacity', current === '1' ? '0.7' : '1')
    }
    this._remaining -= 500
    if (this._remaining <= 0) this._renderText()
  }

  private _finish(): void {
    if (this._finished) return
    this._finished = true
    this._remaining = 0
    this._renderText()
    if (this._interval) {
      clearInterval(this._interval)
      this._interval = null
    }
    this.dispatchEvent(new CustomEvent('finished', { bubbles: true, composed: true }))
    this._resolveFinished()
  }

  private _clearTimers(): void {
    if (this._timeout) {
      clearTimeout(this._timeout)
      this._timeout = null
    }
    if (this._interval) {
      clearInterval(this._interval)
      this._interval = null
    }
  }

  private _applyDurationVar(): void {
    this.style.setProperty('--timed-title-time-left', `${this.duration}ms`)
  }

  private _renderText(): void {
    if (!this._text) return
    const isDone = this._finished || this._remaining <= 0
    this._text.textContent = ''
    if (isDone) {
      this._text.className = 'page-loader__text is-done'
      if (this.doneLabel) this._text.textContent = this.doneLabel
      const hint = this.doneHint
      if (hint) {
        const span = document.createElement('span')
        span.className = 'page-loader__text-muted'
        span.textContent = hint
        this._text.appendChild(span)
      }
    } else {
      this._text.className = 'page-loader__text'
      if (this.label) this._text.textContent = this.label
    }
    // Hide the row when a caller explicitly blanks the copy, so the splash
    // doesn't leave an empty gap under the bar.
    this._text.style.display = (this._text.textContent ?? '').length > 0 ? '' : 'none'
  }

  private _build(): void {
    if (this._built) return
    this._built = true
    const root = this.shadowRoot as ShadowRoot

    const style = document.createElement('style')
    style.textContent = STYLES
    root.appendChild(style)

    const sparks = document.createElement('div')
    sparks.className = 'page-loader__sparks'
    sparks.setAttribute('aria-hidden', 'true')
    for (let i = 0; i < SPARK_COUNT; i++) {
      const spark = document.createElement('span')
      spark.className = 'spark'
      const size = `${3 + (i % 3)}px`
      spark.style.left = `${(i * 29) % 100}%`
      spark.style.width = size
      spark.style.height = size
      spark.style.animationDelay = `${((i * 17) % 40) / 10}s`
      spark.style.animationDuration = `${3.6 + ((i * 11) % 20) / 10}s`
      sparks.appendChild(spark)
    }
    root.appendChild(sparks)

    const inner = document.createElement('div')
    inner.className = 'page-loader__inner'

    // The glow lives with the mark and is centred on it, so it always sits
    // behind the logo regardless of viewport size or layout.
    const markWrap = document.createElement('div')
    markWrap.className = 'page-loader__mark-wrap'

    const glow = document.createElement('div')
    glow.className = 'page-loader__glow'
    glow.setAttribute('aria-hidden', 'true')
    markWrap.appendChild(glow)

    const mark = document.createElement('div')
    mark.className = 'page-loader__mark'
    mark.innerHTML = MARK_SVG
    this._banana = mark.querySelector<SVGElement>('.banana')
    markWrap.appendChild(mark)

    inner.appendChild(markWrap)

    const box = document.createElement('div')
    box.className = 'page-loader__box'

    const barWrapper = document.createElement('div')
    barWrapper.className = 'page-loader__bar-wrapper'
    const bar = document.createElement('div')
    bar.className = 'page-loader__bar'
    barWrapper.appendChild(bar)
    box.appendChild(barWrapper)

    const text = document.createElement('p')
    text.className = 'page-loader__text'
    this._text = text
    box.appendChild(text)

    inner.appendChild(box)
    root.appendChild(inner)

    // Status region for assistive tech (don't clobber a caller's overrides).
    if (!this.hasAttribute('role')) this.setAttribute('role', 'status')
    if (!this.hasAttribute('aria-live')) this.setAttribute('aria-live', 'polite')
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'banana-games-loader': BananaGamesLoaderElement
  }
}
